import type { IPartialDocumentData } from "@shared/common/node/type";
import type { ICampaignListJson } from "@shared/common/types/campaign";
import type { VideoInfo } from "@shared/common/types/data-source";
import { State, type StateType } from "./state";

/** 캐시를 가져옵니다. 없다면 새로 생성합니다. */
export function getCache(): Map<string, State<unknown>> {
  if (!window.CodenButter) {
    window.CodenButter = () => {};
  }

  if (window.CodenButter.jsonCache) {
    return window.CodenButter.jsonCache;
  }

  window.CodenButter.jsonCache = new Map();
  return window.CodenButter.jsonCache;
}

/** 캐시된 State를 가져옵니다. 없다면 새로 생성합니다. */
function getStateFromCache<T>(key: string): State<T> {
  const cache = getCache();
  const stateOrUndefined = cache.get(key) as State<T> | undefined;
  if (stateOrUndefined) {
    return stateOrUndefined;
  }
  const newState = new State<T>();
  cache.set(key, newState as State<unknown>);
  return newState;
}

/** State 로부터 데이터를 가져옵니다. State가 초기화되어 있지 않다면 fetch를 통해 데이터를 가져옵니다. */
export async function getJson<T>(url: string): Promise<T> {
  const state = getStateFromCache<T>(url);

  if (state.getState().type === "initialized") {
    state.set(
      fetch(url).then<T>((response) => {
        if (!response.ok) {
          throw new Error(
            `[codenbutter] getJson Error: ${url} ${response.statusText}`
          );
        }
        return response.json();
      })
    );
  }

  return state.get();
}

export async function setJson<T>(url: string, data: Promise<T>): Promise<void> {
  const state = getStateFromCache<T>(url);
  return state.set(data);
}

/** JSON의 상태가 변할 때마다 특정 동작 수행 */
export function subscribeJson<T>(
  url: string,
  callback: (data: StateType<T>) => void
) {
  return getStateFromCache<T>(url).subscribe(callback);
}

export function preloadJson(url: string): void {
  // eslint-disable-next-line no-console
  getJson(url).catch(console.error);
}

export const getDocumentJson = ({
  siteId,
  campaignId,
}: {
  siteId: string;
  campaignId: string;
}) => {
  return getJson<IPartialDocumentData>(
    `${CONFIG_BUCKET_BASE_URL}/site/${siteId}/${campaignId}.json`
  );
};

export const getCampaignListJson = ({ siteId }: { siteId: string }) => {
  return getJson<ICampaignListJson>(
    `${CONFIG_BUCKET_BASE_URL}/site/${siteId}/campaigns.json`
  );
};

export const getVideoJson = ({ filename }: { filename: string }) => {
  return getJson<VideoInfo>(`${CONFIG_BUCKET_BASE_URL}/video/${filename}.json`);
};
